import * as React from "react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";

export type VbControlSize = "sm" | "md" | "lg";

export const CONTROL_SIZE_CLASS: Record<VbControlSize, string> = {
  sm: "vb-input--sm",
  md: "", // base .vb-input is md (38px)
  lg: "vb-input--lg",
};

/** `size` shadows the native input attribute (a character count nobody uses
 *  here), so it is omitted and reused for the design-system scale. */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: VbControlSize;
  /** Force the error look. Inside a Field/FormRow with `error` set, this is
   *  already on — pass it only for a standalone control. */
  error?: boolean;
};

/** Design-system text input. 38px / 8px radius / strong hairline by default;
 *  sm (30px) and lg (42px) exist for the surfaces built at those heights. */
export const Input = React.forwardRef<HTMLInputElement, Props>(
  ({ size = "md", error, className, id, "aria-describedby": describedBy, ...props }, ref) => {
    const f = useFieldControl({ id, describedBy, invalid: error });

    return (
      <input
        ref={ref}
        id={f.id}
        aria-describedby={f.describedBy}
        aria-invalid={f.invalid || undefined}
        className={cn("vb-input", CONTROL_SIZE_CLASS[size], f.invalid && "vb-input--error", className)}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export default Input;
